/**
 * ModelOverrideLink — the per-turn model override, collapsed to one link.
 *
 * WHY THIS EXISTS (fleet owner, after #2422 relabelled the same control):
 *
 *   "We don't need a per turn model selector for the Workflow Assistant to be
 *    prominent. This would barely ever get used and should be a link to a modal
 *    for selection and not this constant use of real estate."
 *
 * That is a correct reading of what the control is for. ADR-183 Phase 6's
 * override answers ONE authoring turn on a different vendor so "did switching
 * help" is answerable — an experiment run occasionally, not a setting touched
 * every turn. Rendered inline it charged every author, on every turn, for a
 * control almost none of them want: a label, a 16rem <select>, a "N of 4 vendors
 * unavailable" line and, when the catalog cannot be read, an error — all of it
 * above the box where the actual work is typed.
 *
 * WHAT IS NOT COLLAPSED, DELIBERATELY
 * -----------------------------------
 * An override that is IN FORCE stays visible and names its model. Hiding it
 * would leave an operator authoring on a vendor they selected once and forgot,
 * with the run recording a model the surface never showed them — an override is
 * held per page (see Workflows.tsx) precisely so it survives several turns, so
 * "you are not on the default" is a fact the composer owes them. Collapsing the
 * MENU is the ask; collapsing the STATE would be a different, worse change.
 *
 * EVERY REFUSAL AND REASON MOVES WITH THE PICKER, NOTHING IS DROPPED
 * -----------------------------------------------------------------
 * The dialog renders the same <ModelPicker> with the same `gate`, so the
 * four-way "why is this vendor unavailable" reporting, the Console provisioning
 * links and the `unknown`-vs-`missing` distinction are all intact — they are now
 * read by whoever opened the menu rather than by everyone who opened the editor.
 */
import { useEffect, useRef, useState } from 'react'

import { ModelPicker, type ModelChoice } from './ModelPicker'
import type { AssistantModelView, ModelCatalogResponse } from '../api/client'

export function ModelOverrideLink({
  value,
  onChange,
  catalog,
  loading,
  disabled,
  /** Which availability fact gates the vendor list. Defaults to `mc_callable`,
   *  the assistant's gate: this call is placed by Mission Control itself, so a
   *  key that is in 1Password but not in THIS container must not be offered.
   *  Passed straight through — this component adds no availability logic of its
   *  own, or it would become a second source of truth for the one thing the
   *  picker exists to get right. */
  gate = 'mc_callable',
  /** ADR-194 — the STANDING pin, so the dialog can offer "make this my
   *  default" beside "answer this turn with it". Optional: without it this is
   *  exactly the ADR-183 Phase 6 control it has always been. */
  pin = null,
  onPinMine,
  onClearPin,
  /** ADR-194 D2/D7 — composing a FLEET-wide pin is a fleet_owner act. Absent,
   *  the control is not rendered at all rather than rendered and refused. */
  onPinFleet,
  proposal = null,
}: {
  value: ModelChoice
  onChange: (next: ModelChoice) => void
  catalog: ModelCatalogResponse | null
  loading?: boolean
  disabled?: boolean
  gate?: 'selectable' | 'mc_callable'
  pin?: AssistantModelView | null
  onPinMine?: (next: ModelChoice) => void
  onClearPin?: () => void
  onPinFleet?: (next: ModelChoice) => void
  proposal?: { pr_number: number; url: string } | null
}) {
  const [open, setOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement | null>(null)

  // An override is ACTIVE when a model is named. The empty pair is the "no
  // override" sentinel, not a missing value.
  const active = Boolean(value.model.trim())

  // ESC CLOSES, and it is bound to the document rather than to the overlay.
  // The overlay is not focused on open — the select inside it is — so a
  // handler on the overlay would only fire for someone who had tabbed back out
  // to it, i.e. never in the flow this dialog is built for.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Focus the picker on open, so the menu is operable from the keyboard without
  // tabbing through the composer behind it. Guarded: `focus` is absent in some
  // test environments and this must never be the thing that throws.
  useEffect(() => {
    if (!open) return
    const el = selectRef.current?.querySelector('select')
    try { el?.focus() } catch { /* focus is a nicety, never a failure */ }
  }, [open])

  return (
    <span className="ml-auto flex items-center gap-1">
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        /* The trigger states what is in force, so the collapsed form is still
           an honest report and not just an entry point. Indigo when overridden
           because it is a deviation an author should notice in peripheral
           vision; grey and dotted when not, because at rest this is the least
           important thing on the row. */
        className={active
          ? 'text-[11px] text-indigo-700 underline decoration-dotted disabled:opacity-50'
          : 'text-[11px] text-gray-500 underline decoration-dotted hover:text-gray-700 disabled:opacity-50'}
        title={active
          ? `This turn will be answered by ${value.model}. Click to change it or go back to the default.`
          : 'Answer one authoring turn on a different model, to compare them.'}
      >
        {active ? `Answering with ${value.model}` : 'Answer this turn with…'}
      </button>
      {active && (
        /* A ONE-CLICK WAY BACK. The default is also the first option inside the
           dialog, but the baseline is the state an operator returns to after
           every comparison, and making them reopen a menu to undo something
           they can see is how a stale override survives. */
        <button
          type="button"
          onClick={() => onChange({ provider: '', model: '' })}
          disabled={disabled}
          aria-label="Use the default model again"
          title="Use the default model again"
          className="text-[11px] text-gray-400 hover:text-gray-700 disabled:opacity-50"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Answer this turn with"
            className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Answer this turn with</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* The sentence #2422 was really asking for, now that there is
                  room for it: what this does, and what it is NOT. A model
                  belongs to an AGENT (config/fleet.yaml `model.default`, with
                  ADR-184's core-role default behind it) and cannot be set per
                  workflow — so the control says so where it is used, rather
                  than relying on a label to carry the whole distinction. */}
              <p className="text-xs text-gray-600">
                Overrides the model for your next authoring turn in this editor, so the
                same task can be run twice and compared. It is not a property of the
                workflow, and it does not change any agent's model — an agent's model
                lives in its profile and answers turns on the agent's own host.
              </p>
              {/* ADR-194 — THE SAME CHOICE, MADE TO STICK. The turn override
                  above answers once; this keeps it. Both live in one dialog
                  because "which model" is one question, and splitting it across
                  two surfaces is how an operator ends up re-picking on every
                  edit — measured: 25 of 25 runs on the default while four
                  profiles named another vendor.

                  THE FLEET'S PIN IS READ-ONLY HERE. It is committed
                  configuration, changed by a reviewed pull request through the
                  Fleet Editor, so this names the file rather than offering a
                  write that would put the database and the checkout in
                  disagreement. */}
              {pin && (
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="text-xs text-gray-600">
                    {pin.operator.model
                      ? <>You have pinned <span className="font-medium">{pin.operator.model}</span> for yourself.</>
                      : pin.fleet.model
                        ? <>This fleet pins <span className="font-medium">{pin.fleet.model}</span> for everyone.</>
                        : <>Nothing is pinned; turns run on <span className="font-medium">{pin.platform.model}</span>.</>}
                  </div>
                  <div className="flex items-center gap-2">
                    {onPinMine && (
                      <button
                        type="button"
                        disabled={!value.model}
                        onClick={() => { onPinMine(value); setOpen(false) }}
                        className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        title={value.model
                          ? 'Keep answering with this model until you change it'
                          : 'Choose a model above first'}
                      >
                        Keep this as my model
                      </button>
                    )}
                    {onPinFleet && (
                      <button
                        type="button"
                        disabled={!value.model}
                        onClick={() => onPinFleet(value)}
                        className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        title={value.model
                          ? 'Open a pull request pinning this model for everyone on this fleet'
                          : 'Choose a model above first'}
                      >
                        Pin for this fleet…
                      </button>
                    )}
                    {onClearPin && pin.operator.model && (
                      <button
                        type="button"
                        onClick={() => { onClearPin(); setOpen(false) }}
                        className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                        title="Go back to this fleet's model, or the deployment default"
                      >
                        Clear my pin
                      </button>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    This fleet's pin lives in <code>{pin.fleet_path}</code> and changes
                    through a reviewed pull request.
                  </div>
                  {proposal && (
                    // WHERE TO APPROVE IT. A proposal an operator cannot find is
                    // the same as no proposal — this is the other half of the
                    // control, not a nicety.
                    <div className="text-xs text-green-700">
                      Opened{' '}
                      <a className="underline" href={proposal.url} target="_blank" rel="noreferrer">
                        PR #{proposal.pr_number}
                      </a>
                      {' '}— approve it to make this the fleet's model.
                    </div>
                  )}
                </div>
              )}
              <div ref={selectRef}>
                <ModelPicker
                  id="assistant-model"
                  value={value}
                  onChange={next => {
                    onChange(next)
                    // CLOSE ON CHOOSING. The dialog holds exactly one decision,
                    // and a "Done" button after it would be a second click on
                    // the only path anyone takes. Reading a vendor's
                    // unavailability reason changes nothing, so that visit ends
                    // with Esc or the backdrop instead.
                    setOpen(false)
                  }}
                  catalog={catalog}
                  loading={loading}
                  disabled={disabled}
                  gate={gate}
                  /* No literal label: the picker names the actual default model
                     off the catalog, because "this fleet's configured model"
                     withheld the one fact an operator needs in order to compare
                     two models at all (#2422). */
                  defaultOptionNamesCatalogDefault
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </span>
  )
}
